import { fixImageUrl, formatDate } from '../api'
import { useTheme } from '../theme'
import { avecAlpha } from '../lib/couleurs'

// Identite visuelle du module qui PRESENTE l'evenement (DESIGN-SYSTEM v4 MODULE_THEME).
// Les evenements du hub viennent du lieu « Évènements » (ADR-062) ; le TYPE de
// chaque evenement (marche, festival, village...) vient de `typeConfig`, plus bas.
const MODULE_THEME = {
  MARCHE: { c: 'rgb(var(--c-teal))', e: '🎪', l: 'Évènements' },
  TOURNEE: { c: 'rgb(var(--c-primary))', e: '🧺', l: 'Paniers' },
  JADEN: { c: '#5e9e4e', e: '🥑', l: 'Jaden' },
}
const modTheme = (code) => MODULE_THEME[code] || { c: 'rgb(var(--c-green))', e: '•', l: 'Manrina' }

// Carte evenement publique cliquable (hub client : accueil + explorer).
// Plus simple que pages/producer/EventCard.jsx : pas d'inscription, pas d'actions, juste la fiche cliquable.
function partenaireBadge(type, isWeb3) {
  const map = {
    ORGANISATEUR: { label: 'Organisateur', cls: isWeb3 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-50 text-emerald-700' },
    PARTENAIRE: { label: 'Partenaire', cls: isWeb3 ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-50 text-blue-700' },
    COLLABORATEUR: { label: 'Collaborateur', cls: isWeb3 ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-50 text-amber-700' },
  }
  return map[type] || map.COLLABORATEUR
}

// Carte en ligne (style prototype manrina-app-complete.jsx) : tuile a gauche
// (banniere ou degrade + emoji du type), tag/titre/sous-titre au centre,
// chevron a droite. Bandeau partenaires compact en pied si presents.
function EventCardPublic({ event, onClick, variant = 'default', module = 'MARCHE' }) {
  const { isWeb3 } = useTheme()
  const tc = 'text-ink'
  const ts = isWeb3 ? 'text-white/60' : 'text-ink/55'
  const tm = isWeb3 ? 'text-white/60' : 'text-ink/55'

  const isFeatured = variant === 'featured'
  const mod = modTheme(module)

  const typeColor = event?.typeConfig?.couleur || 'rgb(var(--c-primary))'
  const typeEmoji = event?.typeConfig?.icone || '🎪'
  const typeName = event?.typeConfig?.nom || 'Évènement'
  const banner = fixImageUrl(event?.bannerImage)

  const order = { ORGANISATEUR: 0, PARTENAIRE: 1, COLLABORATEUR: 2 }
  const partenaires = (event?.intervenants || [])
    .slice()
    .sort((a, b) => (order[a.type] ?? 9) - (order[b.type] ?? 9))

  const tileSize = isFeatured ? 'w-[52px] h-[52px]' : 'w-11 h-11'
  const horaire = event?.heureDebut ? `${event.heureDebut}${event.heureFin ? '–' + event.heureFin : ''}` : null
  const dateStr = [formatDate(event?.date), horaire].filter(Boolean).join(' · ')

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClick?.()}
      className={`${isFeatured ? 'rounded-[24px] p-4' : 'rounded-[20px] p-3.5'} animate-fade-up cursor-pointer active:scale-[0.98] transition-transform ${
        'panneau-verre'
      }`}
      // S62 : plus de barre d'accent a gauche (retour Daniel) — la couleur du
      // module vit dans la tuile et le chip, la carte reste sobre.
      style={isFeatured && !isWeb3 ? { border: '1.5px solid rgb(var(--c-primary) / 0.5)' } : undefined}
    >
      <div className="flex gap-3.5 items-center">
        {/* Tuile : banniere ou degrade + emoji du type */}
        <div
          className={`${tileSize} ${isFeatured ? 'rounded-[18px]' : 'rounded-[14px]'} flex-shrink-0 flex items-center justify-center overflow-hidden`}
          style={banner ? {
            backgroundImage: `url(${banner})`, backgroundSize: 'cover', backgroundPosition: 'center',
          } : {
            background: `linear-gradient(135deg, ${typeColor}, rgb(var(--c-green)))`,
            boxShadow: '0 8px 18px rgb(var(--c-green) / 0.18)',
          }}
        >
          {!banner && <span className={isFeatured ? 'text-[26px]' : 'text-[22px]'}>{typeEmoji}</span>}
        </div>

        {/* Centre : tag / titre / sous-titre */}
        <div className="flex-1 min-w-0">
          {/* Badge du module qui presente l'evenement (emoji + nom + couleur) */}
          <div className="flex items-center gap-1.5 mb-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={{ background: avecAlpha(mod.c, 0.13), color: mod.c }}>
              {mod.e} {mod.l}
            </span>
            {event?.featured && (
              <span className="text-[9px] font-extrabold uppercase tracking-[0.14em]" style={{ color: 'rgb(var(--c-primary))' }}>🌟 À la une</span>
            )}
          </div>
          <p className={`${isFeatured ? 'text-[14.5px]' : 'text-[13.5px]'} font-bold leading-snug truncate ${tc}`}>
            {event?.nom || event?.lieu || 'Évènement'}
          </p>
          <p className={`text-[11.5px] mt-0.5 truncate ${ts}`}>
            {[event?.lieu && `📍 ${event.lieu}`, dateStr].filter(Boolean).join(' · ') || 'Bientôt'}
          </p>
        </div>

        {/* Droite : nb stands (pill) ou chevron */}
        <div className="flex-shrink-0 flex items-center">
          {event?.participantsCount > 0 ? (
            <span className="text-[11px] font-bold text-green bg-primary/20 rounded-full px-2.5 py-1.5 whitespace-nowrap">
              {event.participantsCount} stand{event.participantsCount > 1 ? 's' : ''}
            </span>
          ) : (
            <span className={`text-lg ${tm}`}>›</span>
          )}
        </div>
      </div>

      {/* Bandeau partenaires (compact) */}
      {partenaires.length > 0 && (
        <div className={`mt-2.5 pt-2.5 border-t border-ink/10`}>
          <div className="flex flex-wrap gap-1.5">
            {partenaires.slice(0, 3).map(p => {
              const badge = partenaireBadge(p.type, isWeb3)
              const photo = fixImageUrl(p.photo)
              return (
                <span
                  key={p.id}
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] ${badge.cls}`}
                  title={`${badge.label}${p.role ? ' · ' + p.role : ''}`}
                >
                  {photo ? (
                    <img src={photo} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
                  ) : (
                    <span className="w-3.5 h-3.5 rounded-full bg-current opacity-30" />
                  )}
                  <span className="font-medium">{p.nom}</span>
                </span>
              )
            })}
            {partenaires.length > 3 && (
              <span className={`text-[11px] ${tm} self-center`}>+{partenaires.length - 3}</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default EventCardPublic
